/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { View, StyleSheet } from 'react-native';
import Toast from 'react-native-toast-message';
import { hasNoAccess, exportCsvApi } from '@global/helpers';
import { isWeb } from '@global/screens';
import Colors from '@global/colors';
import UserCreateOption from '@/screens/setting/users/components/UserCreateOption';
import Button from '@components/form/Button';
import SociollaText from '@components/sociolla/SociollaText';
import Photo from '@components/global/Photo';
import Alert from '@components/global/Alert';
import Table from '@components/table/Table';
import StatusPicker from '@components/form/StatusPicker';
import SalesTeamSearch from '@components/form/search/SalesTeamSearch';
import * as Layout from '@components/screen/Layout';
import TableMobile from '@components/table/TableMobile';
import { showPopup } from '@/store/modal';
import { SALESMAN_TYPE_OBJ } from '@/constants';
import StatusBadge from '@components/global/StatusBadge';

import { MsAccount } from '@api/ms-account';
import * as Resources from '@/store/resources/actions';
import { filterAdded, paramsReseted } from '@/store/params';
import { showAlert } from '@/store/global';
import useListScreen from '@/hooks/useListScreen';
import {
  useGetUsersListQuery,
  useGetUsersCountQuery,
  useGetUsersInfiniteListInfiniteQuery,
} from '@api/listApi';

const photoSize = 40;

// UI filter shape → API query filter (was the inline transform in the old doSearch):
//  - `name` search expands to an $or over name + email
//  - `is_show_all` is true unless a specific is_active filter is applied
const toApiFilter = (rawFilter = {}) => {
  const filter = { ...rawFilter };
  if (filter.name) {
    const nameFilter = filter.name?.$regex
      ? filter.name
      : { $regex: filter.name, $options: 'i' };
    filter.$or = [{ name: nameFilter }, { email: nameFilter }];
    delete filter.name;
  }
  filter.is_show_all = filter.is_active !== undefined ? false : true;
  return filter;
};

const UsersScreen = (props) => {
  const dispatch = useDispatch();
  const isShowUserCreateOption = useSelector(
    (state) => state.modal.userCreateOption,
  );
  const showFilter = useSelector((state) => state.modal.showFilter);
  const roles = useSelector((state) => state.resources.roles);

  const hasAccess = !hasNoAccess(['superadmin']);
  const hasExportAccess = !hasNoAccess(['superadmin', 'general']);

  const { list, count, openChild, scope, params } = useListScreen({
    listHook: useGetUsersListQuery,
    countHook: useGetUsersCountQuery,
    infiniteHook: useGetUsersInfiniteListInfiniteQuery,
    toFilter: toApiFilter,
  });

  const [exporting, setExporting] = useState(false);

  const statuses = [
    { name: 'Active', value: true },
    { name: 'Inactive', value: false },
  ];

  // Role options (reference data) — fetch once on mount.
  useEffect(() => {
    dispatch(Resources.getRoles({}));
  }, [dispatch]);

  const tableStructure = {
    name: {
      head: 'Name',
      body: 'name',
      type: 'slot',
      textStyle: { fontFamily: 'figtree-medium' },
    },
    sales_team: {
      head: 'Sales Team',
      body: 'sales_team',
      type: 'slot',
    },
    role: {
      head: 'Role',
      body: 'role.name',
    },
    email: {
      head: 'Email',
      body: 'email',
    },
    sales_code: {
      head: 'Sales Code',
      body: 'sales_code',
    },
    salesman_type: {
      head: 'Salesman Type',
      body: 'salesman_type',
    },
    status: {
      head: 'Status',
      body: 'is_active',
      type: 'status_active',
    },
    ...(hasAccess
      ? {
          action: {
            head: 'Action',
            type: 'action',
            actions: [
              {
                name: 'update',
                label: 'EDIT',
                style: {
                  borderColor: Colors.blue3,
                  backgroundColor: Colors.blue3,
                  marginRight: 5,
                },
                textStyle: { color: Colors.white },
              },
              {
                name: 'inactive',
                label: 'INACTIVE',
                style: {
                  borderColor: Colors.blue0,
                  backgroundColor: Colors.blue0,
                },
                textStyle: { color: Colors.white },
              },
              {
                name: 'invite',
                label: 'INVITE',
                style: {
                  borderColor: Colors.blue3,
                  backgroundColor: Colors.white,
                },
                textStyle: { color: Colors.blue3 },
              },
            ],
          },
        }
      : {}),
  };

  // Current search text, derived from the applied name filter.
  const searchValue = useMemo(() => {
    const n = params.filter?.name;
    return n && n.$regex ? n.$regex : n ?? '';
  }, [params.filter]);

  // id mapping + salesman_type enum lookup (was the old doSearch per-item map).
  const tableData = useMemo(() => {
    const raw = isWeb
      ? Array.isArray(list.data)
        ? list.data
        : []
      : (list.data?.pages ?? []).flat();
    return raw.map((item) => {
      const next = { ...item };
      if (item._id) {
        next.id = item._id;
      }
      if (item.salesman_type && SALESMAN_TYPE_OBJ[item.salesman_type]) {
        next.salesman_type = SALESMAN_TYPE_OBJ[item.salesman_type];
      }
      return next;
    });
  }, [list.data]);

  // ── Staged filter draft (role / sales_team / status), seeded on open ────────
  const [draftFilter, setDraftFilter] = useState({});
  const [draftSalesTeam, setDraftSalesTeam] = useState('');
  useEffect(() => {
    if (showFilter) {
      const applied = params.filter || {};
      setDraftFilter(applied);
      setDraftSalesTeam(
        applied?.sales_team?.$elemMatch?.id
          ? { id: applied.sales_team.$elemMatch.id }
          : '',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFilter]);

  const refetchList = () => {
    list.refetch();
    count.refetch();
  };

  const gotToDetail = ({ data }) => {
    openChild('UserDetailScreen', {
      titleScreen: data.name,
      name: data.name,
      id: data._id,
    });
  };

  const changeUserState = async (data) => {
    try {
      if (data && data._id) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        const user = await MsAccount.changeUserState(data._id, {
          is_active: !data.is_active,
        });
        if (user && user.data && user.data.data) {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'Successfully updated user', type: 'success' });
          refetchList();
        } else {
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'Failed to update user', type: 'error' });
        }
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({ text1: 'System error occurred', type: 'error' });
    }
  };

  const onInvite = async (data) => {
    dispatch(showPopup({ name: 'isLoading', value: true }));
    try {
      const inviteUser = await MsAccount.inviteUser(data._id);
      if (inviteUser?.data?.data) {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'Successfully invited user', type: 'success' });
      } else {
        dispatch(showPopup({ name: 'isLoading', value: false }));
        Toast.show({ text1: 'Failed to invite user', type: 'error' });
      }
    } catch (error) {
      dispatch(showPopup({ name: 'isLoading', value: false }));
      Toast.show({
        text1: error?.response?.data?.message || 'System error occurred',
        type: 'error',
      });
    }
  };

  const onMapAction = ({ data, action }) => {
    if (data) {
      if (action.name == 'inactive') {
        dispatch(
          showAlert({
            show: true,
            title: data.is_active ? 'INACTIVE USER' : 'ACTIVE USER',
            description: `Are you sure you want to ${
              data.is_active ? 'in-' : ''
            }active this user?`,
            onCallback: () => changeUserState(data),
          }),
        );
      } else if (action.name == 'update') {
        props.navigation.replace('UserFormScreen', {
          titleScreen: data.name,
          name: data.name,
          id: data._id,
          fromTable: true,
        });
      } else if (action.name == 'invite') {
        dispatch(
          showAlert({
            show: true,
            title: 'INVITE USER',
            description: 'Are you sure invite this user?',
            onCallback: () => onInvite(data),
          }),
        );
      }
    }
  };

  const openCreateOptions = () => {
    dispatch(showPopup({ name: 'userCreateOption', value: true }));
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const payload = {};
      await exportCsvApi('user', payload);
      setExporting(false);
    } catch (error) {
      dispatch(
        showAlert({
          show: true,
          title: 'Error',
          description:
            error?.response?.data?.message || 'System error occurred',
          isConfirm: false,
        }),
      );
      setExporting(false);
    }
  };

  return (
    <Layout.Container>
      {isWeb ? (
        <>
          <Layout.Title title="Users">
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              {hasAccess && (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100, marginRight: 10 }}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      + Add
                    </SociollaText>
                  }
                  onPress={() => openCreateOptions()}
                />
              )}
              {hasExportAccess && (
                <Button
                  type="primary"
                  rounded={true}
                  buttonStyle={{ width: 100 }}
                  disabled={exporting}
                  title={
                    <SociollaText
                      type="medium"
                      style={{ fontSize: 14, color: Colors.white }}>
                      {exporting ? 'Loading...' : 'Export'}
                    </SociollaText>
                  }
                  onPress={() => onExport()}
                />
              )}
            </View>
          </Layout.Title>

          <Table
            filterName={scope}
            name="users"
            structures={tableStructure}
            data={tableData}
            loading={list.isLoading}
            refetching={list.isFetching}
            searchKey="name"
            searchPlaceholder="Search by name, email..."
            searchValue={searchValue}
            hasCallback={true}
            hasNoHide={true}
            totalData={count.data}
            onClickRow={(val) => gotToDetail(val)}
            withSort={true}
            onMapAction={(val) => onMapAction(val)}
            skip={params.skip}
            limit={params.limit}
            slot={{
              name: ({ data, index }) => (
                <View
                  key={index}
                  style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Photo
                    width={photoSize}
                    height={photoSize}
                    image={data.image}
                  />
                  <SociollaText type="medium" style={styles.teamName}>
                    {data.name}
                  </SociollaText>
                </View>
              ),
              sales_team: ({ data, index }) => {
                if (data.sales_team && data.sales_team.length) {
                  const sales = [];
                  for (let i = 0; i < data.sales_team.length; i++) {
                    const tm = data.sales_team[i];
                    if (tm && tm.name) {
                      sales.push(tm.name);
                    }
                  }
                  return (
                    <SociollaText key={index} style={{ fontSize: 12 }}>
                      {sales && sales.length ? sales.join(',') : '-'}
                    </SociollaText>
                  );
                } else {
                  return (
                    <SociollaText key={index} style={{ fontSize: 12 }}>
                      -
                    </SociollaText>
                  );
                }
              },
            }}
          />
        </>
      ) : (
        <TableMobile
          filterName={scope}
          searchKey="name"
          placeholder="Search by name, email"
          searchValue={searchValue}
          loading={list.isLoading}
          refetching={list.isFetching}
          loadingLoadMore={list.isFetchingNextPage}
          hasNextPage={list.hasNextPage}
          onLoadMore={list.fetchNextPage}
          onRefresh={list.refetch}
          data={tableData}
          totalData={count.data}
          renderItem={({ data, index }) => {
            const roleName = data.role?.name || '';
            const salesNames =
              data.sales_team && Array.isArray(data.sales_team)
                ? data.sales_team
                    .filter((a) => a.name)
                    .map((a) => a.name)
                    .join(', ')
                : '';
            return (
              <Layout.ItemMobile
                key={index}
                touchable={true}
                left={true}
                style={{ paddingVertical: 15 }}
                onPress={() => gotToDetail({ data: data })}>
                <Photo
                  width={photoSize}
                  height={photoSize}
                  image={data.image}
                />
                <View style={{ flex: 1, marginRight: 8 }}>
                  <SociollaText type="medium" style={styles.teamName}>
                    {data.name}
                  </SociollaText>
                  {roleName || salesNames ? (
                    // Single line, ellipsis-truncated, so long sales teams never
                    // push the status badge off-screen or wrap the role text.
                    <SociollaText
                      numberOfLines={1}
                      style={{ marginTop: 4, fontSize: 12 }}>
                      {roleName}
                      {roleName && salesNames ? ' | ' : ''}
                      <SociollaText
                        style={{ fontSize: 11, color: Colors.grey1 }}>
                        {salesNames}
                      </SociollaText>
                    </SociollaText>
                  ) : null}
                </View>
                <View style={{ flexShrink: 0 }}>
                  <StatusBadge
                    status={data.is_active ? 'active' : 'in_active'}
                  />
                </View>
              </Layout.ItemMobile>
            );
          }}
        />
      )}
      <Alert />
      <UserCreateOption {...props} show={isShowUserCreateOption} />
      <Layout.Filter
        name="users"
        filterName={scope}
        isLocalVariable={true}
        hasFilterData={Object.keys(draftFilter).length > 0}
        onReset={() => {
          if (list.isFetching) {
            return;
          }
          setDraftFilter({});
          setDraftSalesTeam('');
          dispatch(paramsReseted({ key: scope }));
        }}
        onApply={() => {
          if (list.isFetching) {
            return;
          }
          dispatch(filterAdded({ key: scope, filter: draftFilter }));
          dispatch(showPopup({ name: 'showFilter', value: false }));
        }}>
        <Layout.FilterItem>
          <StatusPicker
            label="Role"
            options={roles}
            labelKey="name"
            trackBy="code"
            value={draftFilter['role.code']}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, 'role.code': val.code }))
            }
          />
        </Layout.FilterItem>
        <Layout.FilterItem lastItem>
          <View style={{ width: isWeb ? '45%' : '100%' }}>
            <SalesTeamSearch
              value={draftSalesTeam}
              onChange={(val) => {
                setDraftSalesTeam(val);
                setDraftFilter((f) => ({
                  ...f,
                  sales_team: { $elemMatch: { id: val.id } },
                }));
              }}
            />
          </View>
        </Layout.FilterItem>
        <Layout.FilterItem>
          <SociollaText style={{ fontSize: 13, marginBottom: 10 }}>
            Status
          </SociollaText>
          <StatusPicker
            label=""
            options={statuses}
            value={draftFilter['is_active']}
            trackBy="value"
            labelKey="name"
            minWidth={80}
            onChange={(val) =>
              setDraftFilter((f) => ({ ...f, is_active: val.value }))
            }
          />
        </Layout.FilterItem>
      </Layout.Filter>
    </Layout.Container>
  );
};

const styles = StyleSheet.create({
  teamName: {
    fontSize: 13,
  },
});

export default UsersScreen;
